/**
 * @fileoverview Current User Hook
 * 
 * Provides the authenticated user object derived from the session.
 * This hook depends on useAuthSession and avoids duplicate auth fetches.
 * 
 * @module hooks/useCurrentUser
 * @version 1.0.0
 * @since 2024-01-01
 * 
 * @architecture
 * ```
 * useAuthSession ← Session data (single source of truth)
 *     │
 *     └── useCurrentUser (this hook) ← Extracts user from session
 *             │
 *             ├── useProfile       ← Extends with profile data
 *             ├── useUserRole      ← Gets user roles
 *             └── Other hooks...
 * ```
 * 
 * @caching
 * Inherits stale/cache times from useAuthSession but disables
 * automatic refetching to prevent redundant API calls.
 * 
 * @example
 * // Basic usage - get current user
 * function UserGreeting() {
 *   const { data: user, isLoading } = useCurrentUser();
 *   
 *   if (isLoading) return <Skeleton />;
 *   if (!user) return null;
 *   
 *   return <span>Hello, {user.email}</span>;
 * }
 * 
 * @example
 * // With user ID for other queries
 * function ProfilePage() {
 *   const { data: user } = useCurrentUser();
 *   const { data: profile } = useProfile(user?.id ?? null);
 *   
 *   // ...
 * }
 * 
 * @see {@link useAuthSession} - Session data source
 * @see {@link useProfile} - Extended profile data
 * @see {@link queryKeys.auth.user} - Query key used
 */

import { useQuery } from "@tanstack/react-query";
import { queryKeys } from "@/lib/query-keys";
import { QUERY_CONFIG } from "@/lib/queryConfig";
import { useAuthSession } from "./useAuthSession";
import type { User } from "@supabase/supabase-js";

/**
 * Get current authenticated user from cached session data.
 * 
 * This hook extracts the user object from the session without making
 * additional API calls. It depends on useAuthSession to provide the
 * session data.
 * 
 * @returns {UseQueryResult<User | null>} React Query result with user data
 * @returns {User | null} returns.data - The current user or null if not authenticated
 * @returns {boolean} returns.isLoading - Whether the user data is being fetched
 * @returns {boolean} returns.isError - Whether an error occurred
 * 
 * @example
 * // Simple auth check
 * const { data: user } = useCurrentUser();
 * const isAuthenticated = !!user;
 * 
 * @example
 * // Access user properties
 * const { data: user } = useCurrentUser();
 * console.log(user?.id);    // UUID
 * console.log(user?.email); // Email address
 * console.log(user?.user_metadata); // Custom metadata
 * 
 * @performance
 * - No additional API calls - derives data from useAuthSession
 * - refetchOnWindowFocus: false - prevents unnecessary refetches
 * - refetchOnMount: false - uses cached session data
 */
export function useCurrentUser() {
  const { data: session } = useAuthSession();
  
  return useQuery({
    queryKey: queryKeys.auth.user(),
    
    /**
     * Extracts user from cached session.
     * Returns null if no active session.
     */
    queryFn: async (): Promise<User | null> => {
      if (!session?.user) return null;
      return session.user;
    },
    
    /** Only run when session is available */
    enabled: !!session,
    
    /** Inherit user profile query config */
    ...QUERY_CONFIG.userProfile,
    
    /** Disable automatic refetching - session handles this */
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
}
